/**
 * Which help experience this store gets: `new` (the Service Desk Help Center)
 * or `legacy` (the original help slide-out and bug icon). The api decides from
 * the signed-in store's number and the pilot list IT edits in Cockpit
 * (Service Desk > Settings > Sweet Spot). Office staff who picked a store in the
 * header get what that store would see, so they can test and support it: the
 * picked store number goes along as `store_no` (a store login ignores it). Any
 * failure, slow answer or odd reply means `legacy`, so a store is never moved
 * onto the new experience by accident.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useHqStore } from './useHqStore';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

export type HelpExperience = 'new' | 'legacy';

const TIMEOUT_MS = 6000;

export async function fetchHelpExperience(pickedStoreNo: number | null = null): Promise<HelpExperience> {
  const query = pickedStoreNo !== null ? `?store_no=${encodeURIComponent(String(pickedStoreNo))}` : '';
  try {
    const res = await Promise.race([
      apiGet<{ data?: { experience?: string } }>(`${API_BASE}/v1/servicedesk/me/experience${query}`),
      new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS)),
    ]);
    return res?.data?.experience === 'new' ? 'new' : 'legacy';
  } catch {
    return 'legacy';
  }
}

/**
 * `loading` until the api has answered (or failed, which settles on legacy).
 * Asks again when the store picked in the header changes, keeping the current
 * answer on screen meanwhile so the page does not flash.
 */
export function useHelpExperience(): HelpExperience | 'loading' {
  const { storeNo } = useHqStore();
  const q = useQuery<HelpExperience, Error>({
    queryKey: ['help-experience', storeNo],
    queryFn: () => fetchHelpExperience(storeNo),
    staleTime: 5 * 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
  return q.data ?? (q.isError ? 'legacy' : 'loading');
}
